import React, { useState, useEffect, useRef, useCallback, memo } from "react";
import { Autocomplete, TextField, CircularProgress, Box, Typography, Paper } from "@mui/material";
import axiosInstance from "../../api/axiosInstance";

/**
 * AsyncProjectAutocomplete
 * 
 * Fetches projects from API with pagination, search, and infinite scroll.
 */
const AsyncProjectAutocomplete = React.forwardRef((props, ref) => {
    const {
        value,
        onChange,
        error = "",
        label = "Project",
        placeholder = "Select Project",
        fullWidth = true,
        size = "small",
        ...rest
    } = props;

    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);

    // Default limit
    const limit = 20;

    // To prevent concurrent fetches
    const isFetchingRef = useRef(false);
    // To prevent redundant fetches on re-open
    const lastFetchedSearchRef = useRef(null);

    // Initial value integration - ensure selected option is in the options list
    useEffect(() => {
        if (value && value.value) {
            setOptions((prev) => {
                if (!prev.find((o) => o.value === value.value)) {
                    return [value, ...prev];
                }
                return prev;
            });
        }
    }, [value]);

    const fetchProjects = useCallback(async (searchStr, pageNum, append = false) => {
        if (isFetchingRef.current) return;

        isFetchingRef.current = true;
        setLoading(true);
        try {
            const params = {
                page: pageNum,
                limit,
                search: searchStr || undefined
            };

            // Track what was successfully requested
            if (pageNum === 1) {
                lastFetchedSearchRef.current = searchStr;
            }

            const response = await axiosInstance.get("api/project", { params });
            const result = response.data;
            const data = result?.data || result;

            let fetchedItems = [];
            if (Array.isArray(data)) {
                fetchedItems = data;
            } else if (data && typeof data === "object") {
                fetchedItems = Array.isArray(data.items) ? data.items
                    : Array.isArray(data.projects) ? data.projects
                        : Array.isArray(data.rows) ? data.rows
                            : Array.isArray(data.results) ? data.results
                                : [];
            }

            const mappedOptions = fetchedItems.map(p => ({
                value: String(p.id || p.Id || p.projectId || ""),
                label: p.name || p.projectName || p.project_name || p.project || `Project #${p.id || p.Id || "?"}`,
                original: p
            }));

            setOptions(prev => {
                if (append) {
                    const existingIds = new Set(prev.map(o => o.value));
                    const newOptions = mappedOptions.filter(o => !existingIds.has(o.value));
                    return [...prev, ...newOptions];
                }

                // If not appending, replace options (but keep the selected value if present)
                let newSet = mappedOptions;
                if (value && value.value) {
                    const hasSelected = newSet.some(o => o.value === value.value);
                    if (!hasSelected) {
                        newSet = [value, ...newSet];
                    }
                }
                return newSet;
            });

            setHasMore(mappedOptions.length === limit);
            setPage(pageNum);
        } catch (error) {
            console.error("Failed to fetch projects for autocomplete:", error);
        } finally {
            setLoading(false);
            isFetchingRef.current = false;
        }
    }, [limit, value]);

    // Handle debounced search and initial load on open
    useEffect(() => {
        const timer = setTimeout(() => {
            if (open && inputValue !== lastFetchedSearchRef.current) {
                fetchProjects(inputValue, 1, false);
            }
        }, 300); // 300ms debounce

        return () => clearTimeout(timer);
    }, [inputValue, open, fetchProjects]);

    const handleScroll = (event) => {
        const listboxNode = event.currentTarget || event.target;
        if (
            listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 20 &&
            !loading &&
            hasMore
        ) {
            fetchProjects(inputValue, page + 1, true);
        }
    };

    return (
        <Autocomplete
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            isOptionEqualToValue={(option, val) => option?.value === val?.value}
            getOptionLabel={(option) => option?.label || ""}
            options={options}
            loading={loading && page === 1}
            value={value || null}
            onChange={(_, newValue) => onChange?.(newValue)}
            onInputChange={(_, newInputValue, reason) => {
                if (reason === 'input' || reason === 'clear') {
                    setInputValue(newInputValue);
                }
            }}
            size={size}
            fullWidth={fullWidth}
            filterOptions={(x) => x} // Disable internal filtering, rely on API search
            ListboxProps={{
                onScroll: handleScroll,
                style: { fontSize: "12px", maxHeight: "250px" } // max-height ensures scrollability
            }}
            PaperComponent={({ children }) => (
                <Paper>
                    {children}
                    {hasMore && (
                        <Box
                            onMouseDown={(e) => {
                                e.preventDefault(); // Prevent input blur
                                fetchProjects(inputValue, page + 1, true);
                            }}
                            sx={{
                                padding: "8px",
                                textAlign: "center",
                                cursor: "pointer",
                                color: "#2563eb",
                                fontSize: "12px",
                                fontWeight: 500,
                                borderTop: "1px solid #e2e8f0",
                                "&:hover": { backgroundColor: "#f8fafc" }
                            }}
                        >
                            {loading && page > 1 ? "Loading..." : "Load more..."}
                        </Box>
                    )}
                </Paper>
            )}
            sx={{
                width: "100%",
                ...rest.sx
            }}
            {...rest}
            renderInput={(params) => (
                <TextField
                    {...params}
                    inputRef={ref}
                    label={label}
                    placeholder={placeholder}
                    error={Boolean(error)}
                    helperText={error || ""}
                    variant="outlined"
                    size={size}
                    fullWidth
                    sx={{
                        '& .MuiInputBase-root': {
                            fontSize: '12px',
                            borderRadius: '8px',
                            backgroundColor: '#f8fafc',
                        },
                    }}
                    slotProps={{
                        htmlInput: {
                            ...params.inputProps,
                            style: { fontSize: "12px" },
                        },
                        input: {
                            ...params.InputProps,
                            endAdornment: (
                                <React.Fragment>
                                    {loading ? <CircularProgress color="inherit" size={16} /> : null}
                                    {params.InputProps.endAdornment}
                                </React.Fragment>
                            ),
                        }
                    }}
                />
            )}
            renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                return (
                    <Box component="li" key={option.value} {...optionProps}>
                        <Typography variant="body2" sx={{ fontSize: "12px" }}>
                            {option.label}
                        </Typography>
                    </Box>
                );
            }}
        />
    );
});

export default memo(AsyncProjectAutocomplete);
